
import { computed, defineComponent, nextTick, onUnmounted, reactive, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { IS_MING_TING_SUBJECT } from '@/project-subject/project-subject'
import { countdownDay, formatTimeStamp } from '@/utils/time'
import {
  MySubscribeService,
  ServiceList,
  ServiceOption
} from '@/views/dashboard/service/my-subscribe/my-subscribe.service'
import { AreaTerminalTypes, AreaType, AreaTypeOption } from '@/views/dashboard/service/_enum/area-type'
import { deleteByField, unique } from '@/utils/tool'
import {
  ServiceStatus,
  ServiceStatusColor,
  ServiceStatusIcon,
  ServiceStatusLabel
} from '@/views/dashboard/service/_enum/service-status'
import CmTag from '@/views/dashboard/_components/cm-tag.vue'
import CmSvgIcon from '@/components/cm-svg-icon/cm-svg-icon.vue'
import ContentAdaptLayout from '@/views/dashboard/_components/content-adapt-layout/content-adapt-layout.vue'
import { ServiceType } from '@/views/dashboard/_enum/service-type'
import { ServiceGroupIcon, ServiceGroupSort } from '@/views/dashboard/service/_enum/service-group'
import {
  subscribeServiceGroupClassMessages,
  subscribeServiceGroupClassNameToSlug,
} from '@/views/dashboard/service/my-subscribe/subscribe-service-group.messages'
import type { SubscribePriceTitleLocale } from '@/views/dashboard/service/my-subscribe/subscribe-price-title.messages'
import { DateKind, DateKindLabel } from '@/views/dashboard/service/_enum/date-kind'
import ServiceAuth from '@/views/dashboard/service/_components/service-auth/service-auth.vue'
import EmptyData from '@/views/dashboard/_components/empty-data/empty-data.vue'
import ServiceBatchAuth from '@/views/dashboard/service/_components/service-batch-auth/service-batch-auth.vue'
import { useRouter } from 'vue-router'
import { ServiceCost, ServiceCostSort } from '@/views/dashboard/service/_enum/service-cost'
import { useReflectiveInjector } from '@tanbo/vue-di-plugin'
import { ToSubscribeByPriceIdParams, toSubscribeByPriceId, BASIC_SERVICE_PERMISSION_NAMES } from '@/services/service-permission.service'
import { HIDE_FUNC } from '@/common/const/hide-function-config'
import { IS_JP_ONLY_BUILD } from '@/common/const/build-locale'
import { AuthLabel, authMode } from '../../cascade-center/cascade-manage/types'
import { IS_PRIVATE } from '@/common/const/env'
import StatisticsCards from '@/views/dashboard/_components/statistics-cards/statistics-cards.vue'
import BuyMoreDrawer from '@/views/dashboard/_components/buy-more-drawer/buy-more-drawer.vue'
import { useSubscribeDetailsDrawerStore } from '@/stores/subscribe-details-drawer.store'
import { openQidianOnlineService } from '@/config/external-link'
import { isAiAssistantSubscribeItem } from '@/views/dashboard/service/service-subscribe/ai-assistant-detail/ai-assistant.service'
import { HomeService } from '@/views/dashboard/home/home.service'
import { StatisticsCardItem } from '../../_components/statistics-cards/types'

interface SubscribeListShowItem {
  class_name: string,
  is_expand: boolean,
  service_list: ServiceList[]
}

export default defineComponent({
  components: { EmptyData, CmTag, CmSvgIcon, ServiceBatchAuth, ServiceAuth, ContentAdaptLayout, StatisticsCards, BuyMoreDrawer },
  setup (props, context) {
    const { t, te, locale } = useI18n()
    /** 按 price_id / package_price_id 走多语言，缺 key 时回退接口 display_name */
    function subscribeServiceDisplayName (item: ServiceList): string {
      void locale.value
      const id = item.price_id ?? item.package_price_id
      if (id == null) return item.display_name || ''
      const key = `mySubscribe.priceTitle.${id}`
      const translated = te(key) ? String(t(key)) : ''
      return translated || item.display_name || ''
    }

    /**
     * 接口返回的分组 class_name（固定中文）→ 多语言展示名。
     * 直接从 subscribeServiceGroupClassMessages 取当前语言文案，避免 te() 对嵌套键误判导致一直回退中文。
     */
    function subscribeServiceGroupDisplayName (className: string | undefined): string {
      void locale.value
      if (!className) return ''
      const raw = className.trim()
      const slug = subscribeServiceGroupClassNameToSlug[raw] ?? subscribeServiceGroupClassNameToSlug[className]
      if (!slug) return className
      const loc = (['zh-CN', 'zh-TW', 'en-US', 'ja-JP'].includes(locale.value)
        ? locale.value
        : 'zh-CN') as SubscribePriceTitleLocale
      const text = subscribeServiceGroupClassMessages[loc]?.[slug]
      return text ?? className
    }
    const injector = useReflectiveInjector([MySubscribeService, HomeService])
    const service = injector.get(MySubscribeService)
    const homeService = injector.get(HomeService)
    const subscribeDetailsDrawerStore = useSubscribeDetailsDrawerStore()

    const subscribeTerminalStats = reactive({
      buyCount: 0,
      exceedCount: 0,
      expireCount: 0,
    })

    const subscribeStatsCardItems = computed((): StatisticsCardItem[] => {
      void locale.value
      return [
        { label: t('mySubscribe.statsSubscribed'), value: subscribeTerminalStats.buyCount, span: 8 },
        { label: t('mySubscribe.statsServiceExceed'), value: subscribeTerminalStats.exceedCount, span: 8,clickable:true },
        { label: t('mySubscribe.statsServiceExpired'), value: subscribeTerminalStats.expireCount, span: 8,clickable:true },
      ]
    })

    const statsTerminalSub = homeService.updateStatsTerminalDataEvent.subscribe((res) => {
      subscribeTerminalStats.buyCount = res.buy_count || 0
      subscribeTerminalStats.exceedCount = res.exceed_count || 0
      subscribeTerminalStats.expireCount = res.expire_count || 0
    })

    onUnmounted(() => {
      statsTerminalSub.unsubscribe()
    })

    const router = useRouter()
    const serviceStatusLabelMap = computed(() => ({
      [ServiceStatus.noUse]: t('mySubscribe.statusNoUse'),
      [ServiceStatus.using]: t('mySubscribe.statusUsing'),
      [ServiceStatus.completed]: t('mySubscribe.statusCompleted'),
      [ServiceStatus.trying]: t('mySubscribe.statusTrying'),
    }))
    const dateKindLabelMap = computed(() => ({
      [DateKind.day]: t('orderHistory.day'),
      [DateKind.month]: t('orderHistory.month'),
      [DateKind.quarter]: t('orderHistory.quarter'),
      [DateKind.year]: t('orderHistory.year'),
    }))

    const activeTab = ref(AreaType.normal)
    const isLoading = ref(false)
    const serviceAuthVisible = ref(false)
    const serviceBatchAuthVisible = ref(false)
    const buyMoreDrawerVisible = ref(false)

    /** 第三张卡为「服务到期」，打开订阅详情批量续订 */
    function handleStatsCardClick (index: number) {
      if (hideSubscribe.value && index === 2) {
        return
      }
      if (index === 2) {
        subscribeDetailsDrawerStore.open({}, { renewAll: true })
      } else {
        buyMoreDrawerVisible.value = true
      }
    }
    const expandIndex = ref([-1, -1])
    const subscribeList = ref<ServiceList[]>([])
    const currentItem = ref({} as ServiceList)
    const subscribeListShow = ref<SubscribeListShowItem[]>([])
    const isBatching = ref(false)
    const selectionData = ref<ServiceList[]>([])
    const isShowSelection = ref(false)
    const serviceOption = ref<ServiceOption[]>([])
    const searchParams = reactive({
      permissionNames: [],
    })

    const selectedCount = computed(() => {
      return selectionData.value.length
    })

    const firstServiceList = computed(() => {
      return subscribeListShow.value[0].service_list
    })

    const isSelectAll = computed(() => {
      const showSelected = selectionData.value.filter(v => firstServiceList.value.find(m => m.permission_name === v.permission_name))
      return showSelected.length === firstServiceList.value.length
    })

    const hideSubscribe = computed(() => {
      return !IS_MING_TING_SUBJECT || HIDE_FUNC.service__subscribe
    })

    function updateData () {
      isLoading.value = true
      return service.getSubscribeList({
        page: 1,
        limit: 99999,
        area_type: activeTab.value,
        permission_names: searchParams.permissionNames[1],
      }).then(response => {
        subscribeList.value = response.service_list
      }).finally(() => {
        isLoading.value = false
        homeService.initStatsTerminal()
      })
    }

    function updateServiceOption () {
      service.getServiceOption({
        area_type: activeTab.value,
      }).then(response => {
        serviceOption.value = response.service_type
      })
    }

    function handleClickService (item: ServiceList, categoryIndex, index) {
      if (isBatching.value) {
        changeCheckbox(item)
        return
      }

      if (item.type === ServiceType.experience) {
        return
      }

      if (expandIndex.value[0] === categoryIndex && expandIndex.value[1] === index) {
        expandIndex.value = [-1, -1]
      } else {
        expandIndex.value = [categoryIndex, index]
      }
    }

    function isPaidSubscribeRow (item: ServiceList): boolean {
      return [ServiceCost.fee, ServiceCost.bonus].includes(item.data_type) || isAiAssistantSubscribeItem(item)
    }

    function openConsultRenew () {
      openQidianOnlineService()
    }

    /** 判断服务是否为免费赠送的基础功能 */
    function isBasicService (item: ServiceList): boolean {
      if (item.permission_name) {
        return BASIC_SERVICE_PERMISSION_NAMES.has(item.permission_name)
      }
      // 通过 price_id 兜底判断：90001~90008 为基础功能
      const id = item.price_id ?? item.package_price_id
      return id != null && id >= 90001 && id <= 90008
    }

    function handleAuth (item: ServiceList) {
      currentItem.value = item
      serviceAuthVisible.value = true
    }

    function handleCancelSelect () {
      selectionData.value = []
      toggleShowSelected(false)
    }

    function handleSelectionChange (values: ServiceList[]) {
      selectionData.value = values
      context.emit('changeSelect', selectionData.value.map(v => v.price_id))
    }

    /**
     * 切换展示已选数据
     * @param show 如果使用了show参数，则是切换到指定状态。不设置表示切换反状态
     */
    function toggleShowSelected (show?: boolean) {
      isShowSelection.value = typeof show === 'boolean' ? show : !isShowSelection.value
      if (isShowSelection.value) {
        subscribeListShow.value[0].service_list = [...selectionData.value]
      } else {
        updateData()
      }
    }

    async function changeServiceOption () {
      isShowSelection.value = false
      await updateData()
      selectionData.value = [...firstServiceList.value]
    }

    function changeCheckbox (item: ServiceList) {
      if (selectionData.value.find(v => v.permission_name === item.permission_name)) {
        selectionData.value = deleteByField(selectionData.value, 'permission_name', item.permission_name)
      } else {
        selectionData.value.push(item)
      }
    }

    function handleSelectAll () {
      if (isSelectAll.value) {
        firstServiceList.value.forEach(v => {
          selectionData.value = deleteByField(selectionData.value, 'permission_name', v.permission_name)
        })
      } else {
        selectionData.value = unique(selectionData.value.concat(firstServiceList.value), 'permission_name')
      }
    }

    function toBatchAuth () {
      serviceBatchAuthVisible.value = true
    }

    function handleBatchSelect () {
      isBatching.value = true
      expandIndex.value = [ -1, -1 ]
      nextTick(() => {
        selectionData.value = [...firstServiceList.value]
      })
    }

    function toSubscribe (item: ServiceList) {
      const params: ToSubscribeByPriceIdParams = { purchase_count: item.count }
      activeTab.value === AreaType.normal && (params.normal_price_id = item.price_id)
      activeTab.value === AreaType.linux && (params.linux_price_id = item.package_price_id)
      toSubscribeByPriceId(params)
    }

    function cancelBatch () {
      selectionData.value = []
      isBatching.value = false
    }

    function handleTabClick () {
      updateData()
      updateServiceOption()
      cancelBatch()
    }

    updateData()
    updateServiceOption()

    watch(buyMoreDrawerVisible, (open) => {
      if (!open) {
        updateData()
      }
    })

    watch(() => [subscribeList.value, isBatching.value], () => {
      if (isBatching.value) {
        subscribeListShow.value = [{
          class_name: '',
          is_expand: true,
          service_list: subscribeList.value.filter(v => v.data_type === ServiceCost.fee)
        }]
      } else {
        let categoryList = Array.from(new Set(subscribeList.value.map(v => v.class_name)))
        categoryList = categoryList.sort((a, b) => ServiceGroupSort.indexOf(a) - ServiceGroupSort.indexOf(b))
        subscribeListShow.value = categoryList.map(v => ({
          class_name: v,
          is_expand: true,
          service_list: subscribeList.value
            .filter(m => m.class_name === v)
            .sort((a, b) => ServiceCostSort.indexOf(a.data_type) - ServiceCostSort.indexOf(b.data_type))
        }))
      }
    }, { immediate: true, deep: true })

    return {
      t,IS_JP_ONLY_BUILD,
      subscribeStatsCardItems,
      serviceStatusLabelMap,
      dateKindLabelMap,
      AreaTypeOption,AuthLabel,authMode,
      AreaTerminalTypes,
      IS_MING_TING_SUBJECT,
      ServiceGroupIcon,
      ServiceStatusIcon,
      ServiceStatusColor,
      ServiceStatus,
      ServiceType,
      ServiceCost,
      AreaType,
      IS_PRIVATE,
      activeTab,
      serviceOption,
      searchParams,
      isBatching,
      isShowSelection,
      hideSubscribe,
      subscribeListShow,
      serviceBatchAuthVisible,
      buyMoreDrawerVisible,
      handleStatsCardClick,
      isLoading,
      subscribeList,
      isSelectAll,
      serviceAuthVisible,
      expandIndex,
      selectionData,
      currentItem,
      selectedCount,
      handleCancelSelect,
      handleSelectAll,
      handleSelectionChange,
      toggleShowSelected,
      updateData,
      toBatchAuth,
      cancelBatch,
      handleBatchSelect,
      countdownDay,
      formatTimeStamp,
      handleAuth,
      isBasicService,
      subscribeServiceDisplayName,
      subscribeServiceGroupDisplayName,
      handleClickService,
      changeCheckbox,
      changeServiceOption,
      toSubscribe,
      handleTabClick,
      isPaidSubscribeRow,
      isAiAssistantSubscribeItem,
      openConsultRenew,
    }
  }
})
